import { types, Instance } from 'mobx-state-tree';
import { webRTCService } from '../services/webrtcService';

/**
 * PeerMessage model for MST
 */
const PeerMessageModel = types.model('PeerMessage', {
  content: types.string,
  timestamp: types.Date,
});

/**
 * PeerConnectionInfo model for MST
 */
const PeerConnectionInfoModel = types.model('PeerConnectionInfo', {
  lastMessageSent: types.maybeNull(PeerMessageModel),
  lastMessageReceived: types.maybeNull(PeerMessageModel),
});

type PeerConnectionInfo = Instance<typeof PeerConnectionInfoModel>;


/**
 * DebugPanelStore model for managing debug panel state
 */
export const DebugPanelStoreModel = types
  .model('DebugPanelStore', {
    peers: types.map(PeerConnectionInfoModel),
    isVisible: types.optional(types.boolean, false),
  })
  .actions((self) => ({
    /**
     * Toggle debug panel visibility
     */
    toggleVisibility() {
      self.isVisible = !self.isVisible;
    },

    /**
     * Set debug panel visibility
     */
    setVisibility(visible: boolean) {
      self.isVisible = visible;
    },

    upsertPeer(peerId: string): PeerConnectionInfo {
      let peer = self.peers.get(peerId);
      if (!peer) {
        peer = PeerConnectionInfoModel.create();
        self.peers.set(peerId, peer);
      }
      return peer;
    },

    removePeer(peerId: string) {
      self.peers.delete(peerId);
    },

    /**
     * Update a specific connection's last sent message
     * Note: Using 'as any' is necessary here because the message comes from external sources
     * as a plain object that matches the structure but isn't an MST instance yet
     */
    updateLastMessageSent(peerId: string, data: any) {
      this.upsertPeer(peerId).lastMessageSent = { content: JSON.stringify(data, null, 2), timestamp: new Date() };
    },

    /**
     * Update a specific connection's last received message
     * Note: Using 'as any' is necessary here because the message comes from external sources
     * as a plain object that matches the structure but isn't an MST instance yet
     */
    updateLastMessageReceived(peerId: string, data: any) {
      this.upsertPeer(peerId).lastMessageReceived = { content: JSON.stringify(data, null, 2), timestamp: new Date() };
    },
  }));

export interface DebugPanelStore extends Instance<typeof DebugPanelStoreModel> { }

/**
 * Singleton instance of DebugPanelStore
 */
export const debugPanelStore = DebugPanelStoreModel.create({
  peers: {},
  isVisible: false,
});

// Register WebRTC message handlers to update the debug panel store

webRTCService.onMessageSent((peerId: string, data: any) => {
  console.log('Message sent to', peerId, data);
  debugPanelStore.updateLastMessageSent(peerId, data);
});

webRTCService.onMessageReceived((peerId: string, data: any) => {
  console.log('Message received from', peerId, data);
  debugPanelStore.updateLastMessageReceived(peerId, data);
});

webRTCService.onConnectionStateChange((peerId, state) => {
  switch (state) {
    case 'disconnected':
    case 'failed':
    case 'closed':
      // For these states, we may want to clear messages or update status
      debugPanelStore.removePeer(peerId);
      break;
  }
});

// webRTCService.onDataChannel((peerId, channel) => {
//   // TODO if required
// });
